import * as React from 'react';

/**
 * Notices when a newer build has been deployed while this tab is still running the old one.
 *
 * The application is a single static bundle: once the browser has it, it never goes back for
 * it, so a deploy leaves every open tab running yesterday's code against today's API. The
 * build writes its identity to version.json (see BuildIdentityPlugin in webpack.common.js)
 * and polling that file is what tells a running tab it has fallen behind.
 */
export interface BuildIdentity {
  version: string;
  commitHash: string;
  buildDate: string;
}

/** Relative to <base href="/">, so it resolves under whatever path the app is served from. */
const VERSION_URL = 'version.json';

/** How often a tab looks. Long: deploys are rare and the prompt is not urgent. */
export const POLL_INTERVAL_MS = 5 * 60 * 1000;

/** Floor between checks, so flicking back to the tab repeatedly is not a request each time. */
const MIN_GAP_MS = 30 * 1000;

const identityOf = (build: BuildIdentity): string =>
  `${build.version}|${build.commitHash}|${build.buildDate}`;

/**
 * The identity of the build currently on the server, or null if it could not be read — the
 * network is down, or a deploy is halfway through swapping the files. Neither is something
 * to tell the user about, so both come back the same way.
 */
export const fetchBuildIdentity = async (): Promise<string | null> => {
  try {
    // no-store rather than a cache-busting query: the point is to see the file as it is now,
    // and a query string would only paper over a cache that should not be caching this.
    const response = await fetch(VERSION_URL, { cache: 'no-store' });
    if (!response.ok) return null;

    const body = await response.json();
    if (!body || typeof body.buildDate !== 'string') return null;

    return identityOf(body as BuildIdentity);
  } catch {
    return null;
  }
};

/**
 * True once the server is serving a different build from the one this tab loaded.
 *
 * The comparison is against the first identity this tab managed to read, not against
 * APP_VERSION: the runtime env-config can override the version the bundle was built with,
 * and comparing the two would report an update that never happened.
 */
export const useIsUpdateAvailable = (pollIntervalMs: number = POLL_INTERVAL_MS): boolean => {
  const [isAvailable, setIsAvailable] = React.useState(false);

  // Refs, not state: neither value should cause a render, and the baseline has to survive
  // the interval callback closing over it.
  const loadedIdentity = React.useRef<string | null>(null);
  const lastCheck = React.useRef(0);

  React.useEffect(() => {
    // Once it is out of date it stays out of date; nothing is left to watch for.
    if (isAvailable) return undefined;

    let cancelled = false;

    const check = async () => {
      const now = Date.now();
      if (now - lastCheck.current < MIN_GAP_MS) return;
      lastCheck.current = now;

      const identity = await fetchBuildIdentity();
      if (!identity) return;

      // Recorded even when this effect has already been cleaned up: the baseline describes
      // the page, not the component, and throwing it away here is how it gets lost for good.
      // StrictMode runs every effect twice in development, so the first run's fetch always
      // lands after its own cleanup, and the second run is inside the throttle window below
      // — drop the late answer and there is no baseline left to compare anything against.
      if (loadedIdentity.current === null) {
        loadedIdentity.current = identity;
        return;
      }

      // Only the state update has to respect the cleanup.
      if (!cancelled && identity !== loadedIdentity.current) setIsAvailable(true);
    };

    void check();

    const timer = window.setInterval(() => void check(), pollIntervalMs);
    // Catches the common case directly: someone comes back to a tab left open over a deploy.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [isAvailable, pollIntervalMs]);

  return isAvailable;
};
